import {
  ControllerFlowAPI,
  ControllerParams,
  CreateControllerFn,
} from '@wix/yoshi-flow-editor';
import { withErrorHandler } from '@wix/bookings-viewer-error-handler';
import { ITEM_TYPES } from '@wix/advanced-seo-utils/api';
import { bookingsCalendarErrorMessages } from '@wix/bi-logger-wixboost-ugc/v2';
import { EmptyStateType } from './ViewModel/emptyStateViewModel/emptyStateViewModel';
import {
  CalendarViewModel,
  createMemoizedCalendarViewModelFactory,
} from './ViewModel/viewModel';
import { CalendarActions, createCalendarActions } from './Actions/actions';
import { CalendarApi } from '../../api/CalendarApi';
import {
  createControlledComponent,
  Render,
} from '../../utils/ControlledComponent/ControlledComponent';
import { createInitialState } from '../../utils/state/initialStateFactory';
import {
  CalendarContext,
  createCalendarContext,
} from '../../utils/context/contextFactory';
import { Preset } from '../../types/types';
import {
  isCalendarPage,
  isCalendarWidget,
  mapPresetToWidgetName,
} from '../../utils/presets';
import {
  getUrlQueryParamValue,
  BookingsQueryParams,
  extractCalendarSelections,
  extractCalendarSelectionsForService,
  isPricingPlanInstalled as isPricingPlanInstalledUtils,
  isMemberAreaInstalled as isMemberAreaInstalledUtils,
  getServiceSlug,
  CALENDAR_PAGE_URL_PATH_PARAM,
  ReservedSlug,
  CalendarSelections,
} from '@wix/bookings-catalog-calendar-viewer-utils';
import { Service } from '@wix/ambassador-bookings-services-v2-service/types';
import { bookingsUoUFlowWidgetLoadSrc16Evid32 } from '@wix/bi-logger-bookings-data/v2';
import { PresetToWidgetNameMap } from '../../utils/bi/consts';
import { CalendarState } from './controller';
import { shouldOpenPreferencesModal } from '@wix/bookings-services-preferences-modal/utils';
import { PanoramaTransactions } from '../../utils/panorama/consts';

export const createControllerFactory = (
  preset: Preset,
  settingsParams: any,
) => {
  const createController: CreateControllerFn = async ({
    flowAPI,
  }: ControllerParams) => {
    const { controllerConfig, experiments, httpClient, errorHandler } = flowAPI;

    withErrorHandler({ httpClient, errorHandler });

    const isCalendarPagePreset = isCalendarPage(preset);
    const isCalendarWidgetPreset = isCalendarWidget(preset);
    const subscriptions: CalendarContext['subscriptions'] = {};
    let rerender: Render<CalendarState> = async () => {};
    let publicData = controllerConfig.config.publicData.COMPONENT || {};
    let calendarContext: CalendarContext;
    let initialState: CalendarState;
    let servicesInView: Service[];
    let pageReady: () => Promise<void>;

    // eslint-disable-next-line prefer-const
    pageReady = async () => {
      const { reportError } = flowAPI;

      const isDateAndTimeViewMode = await isDateAndTimeMode({
        flowAPI,
        preset,
      });
      let calendarSelections: CalendarSelections | undefined =
        isDateAndTimeViewMode
          ? await extractCalendarSelections({
              session: flowAPI.controllerConfig.platformAPIs.storage.session,
            })
          : undefined;

      const panoramaTransaction = flowAPI.panoramaClient.transaction(
        PanoramaTransactions.calendarWidgetInitialization,
      );
      panoramaTransaction.start({
        customParams: {
          preset,
          isSSR: flowAPI.environment.isSSR,
          isEditor: flowAPI.environment.isEditor,
          calendarSelections,
        },
      });

      let queryServicesResponse: any;
      let rescheduleBookingDetails: any;

      try {
        const calendarApi = new CalendarApi({
          reportError,
          flowAPI,
          settingsParams,
          preset,
        });

        const initialErrors: EmptyStateType[] = [];

        const onError = (type: EmptyStateType) => initialErrors.push(type);

        const isAnonymousCancellationFlow =
          getUrlQueryParamValue(
            flowAPI.controllerConfig.wixCodeApi,
            BookingsQueryParams.REFERRAL,
          ) === 'batel';
        const selectedService = flowAPI.settings.get(
          settingsParams.selectedService,
        );
        const shouldUseCalendarDummyViewModel =
          flowAPI.environment.isEditor && selectedService === '';
        const currentUser = controllerConfig.wixCodeApi.user.currentUser;
        const [
          queryServicesResponseResult,
          getBookingsSettingsResponse,
          rescheduleBookingDetailsResult,
          isPricingPlanInstalled,
          isMemberAreaInstalled,
        ] = await Promise.all([
          calendarApi.queryServices({
            onError,
            calendarSelections,
            isDateAndTimeViewMode,
          }),
          calendarApi.getBookingsSettings(),
          calendarApi.getBookingDetails({
            onError: (type: EmptyStateType) => {
              if (!isAnonymousCancellationFlow) {
                onError(type);
              }
            },
          }),
          isPricingPlanInstalledUtils(
            flowAPI.controllerConfig.wixCodeApi,
          ).catch(() => false),
          isMemberAreaInstalledUtils({
            wixCodeApi: flowAPI.controllerConfig.wixCodeApi,
          }).catch(() => false),
        ]);

        queryServicesResponse = queryServicesResponseResult;
        rescheduleBookingDetails = rescheduleBookingDetailsResult;

        const bookingsSettings = getBookingsSettingsResponse.bookingsSettings!;

        servicesInView = queryServicesResponse?.services || [];

        const hasServiceInView = servicesInView.length > 0;
        const hasInitialErrors = initialErrors.length > 0;
        const isPreferencesModalPartOfTheFlow =
          !hasInitialErrors &&
          hasServiceInView &&
          shouldOpenPreferencesModal({
            bookingsSettings,
            service: servicesInView[0],
            isDummyService: shouldUseCalendarDummyViewModel,
            queryParams:
              flowAPI.controllerConfig.wixCodeApi.location.query || {},
            widgetContext: {
              widgetName: mapPresetToWidgetName(preset),
              isEditorMode: flowAPI.environment.isEditor,
              isRescheduleFlow: !!rescheduleBookingDetails,
            },
          });

        if (!isDateAndTimeViewMode && isPreferencesModalPartOfTheFlow) {
          calendarSelections = await extractCalendarSelectionsForService({
            session: flowAPI.controllerConfig.platformAPIs.storage.session,
            serviceId: servicesInView[0].id!,
          });
        }

        const allPurchasedPricingPlans =
          (isCalendarPagePreset || isCalendarWidgetPreset) &&
          (!calendarSelections || calendarSelections.services.length === 1)
            ? await calendarApi.getPurchasedPricingPlans({
                currentUser,
                service: servicesInView.length ? servicesInView[0] : undefined,
              })
            : [];

        initialState = createInitialState({
          wixCodeApi: controllerConfig.wixCodeApi,
          servicesInView,
          rescheduleBookingDetails,
          initialErrors,
          allPurchasedPricingPlans,
          isPricingPlanInstalled,
          isUserLoggedIn: currentUser.loggedIn,
          servicesVariants: queryServicesResponse?.servicesVariants,
          experiments,
          calendarSelections,
          bookingsSettings,
          isDateAndTimeViewMode,
          isPreferencesModalPartOfTheFlow,
          preset,
        });
        calendarContext = await createCalendarContext({
          flowAPI,
          bookingsSettings,
          calendarApi,
          initialState,
          isPricingPlanInstalled,
          isMemberAreaInstalled,
          settingsParams,
          preset,
          calendarSelections,
          reloadWidget: pageReady,
          isDateAndTimeViewMode,
          subscriptions,
        });
        const { biLogger } = calendarContext;
        const referralInfo = getUrlQueryParamValue(
          controllerConfig.wixCodeApi,
          BookingsQueryParams.REFERRAL,
        );
        void biLogger.report(
          bookingsUoUFlowWidgetLoadSrc16Evid32({
            widget_name: PresetToWidgetNameMap[preset],
            referralInfo,
          }),
        );

        const { onStateChange, render, controllerActions, setState } =
          await createControlledComponent<
            CalendarState,
            CalendarActions,
            CalendarViewModel,
            CalendarContext
          >({
            controllerConfig,
            initialState,
            viewModelFactory: createMemoizedCalendarViewModelFactory(
              shouldUseCalendarDummyViewModel,
            ),
            actionsFactory: createCalendarActions,
            context: calendarContext,
          });
        rerender = render;
        if (
          isAnonymousCancellationFlow &&
          !controllerConfig.wixCodeApi.user.currentUser.loggedIn &&
          !flowAPI.environment.isSSR &&
          getUrlQueryParamValue(
            flowAPI.controllerConfig.wixCodeApi,
            BookingsQueryParams.BOOKING_ID,
          )
        ) {
          setTimeout(async () => {
            try {
              // @ts-expect-error
              await controllerConfig.wixCodeApi.user.promptLogin();
            } catch (e) {
              //
            }

            const updatedRescheduleBookingDetails =
              await calendarApi.getBookingDetails({
                onError,
              });
            setState({
              rescheduleBookingDetails: updatedRescheduleBookingDetails,
            });
          }, 10);
        }

        if (isCalendarPagePreset) {
          const seoData = queryServicesResponse?.services?.length
            ? queryServicesResponse?.services[0]
            : undefined;

          await flowAPI.controllerConfig.wixCodeApi.seo.renderSEOTags({
            itemType: ITEM_TYPES.BOOKINGS_CALENDAR,
            itemData: seoData as any,
          });
        }

        if (!flowAPI.environment.isSSR) {
          onStateChange((state) => {
            biLogger.update(state, calendarContext);
          });
        }

        if (isCalendarPagePreset || isCalendarWidgetPreset) {
          flowAPI.controllerConfig.wixCodeApi.user.onLogin(
            controllerActions.onUserLoggedIn,
          );
        }

        const serviceNotFound = initialState.initialErrors.includes(
          EmptyStateType.SERVICE_NOT_FOUND,
        );

        if (serviceNotFound) {
          void biLogger.report(
            bookingsCalendarErrorMessages({
              errorMessage: 'SERVICE_NOT_FOUND',
            }),
          );
        }

        panoramaTransaction.finish({
          customParams: {
            servicesCount: servicesInView?.length,
            calendarSelections,
          },
        });
      } catch (e) {
        flowAPI.panoramaClient
          .errorMonitor()
          .reportError(e instanceof Error ? e : new Error(String(e)), {
            context: {
              preset,
              isSSR: flowAPI.environment.isSSR,
              isEditor: flowAPI.environment.isEditor,
              calendarSelections,
            },
          });
        throw e;
      }
    };

    if (isCalendarPagePreset) {
      controllerConfig.wixCodeApi.location.onChange(() => pageReady());
    }

    return {
      pageReady,
      exports() {
        return {
          onNextClicked(
            overrideCallback: CalendarContext['subscriptions']['onNextClicked'],
          ) {
            subscriptions.onNextClicked = overrideCallback;
          },
        };
      },
      async updateConfig($w, newConfig) {
        const updatedPublicData = newConfig.publicData.COMPONENT || {};
        const isCalendarLayoutChanged =
          publicData.calendarLayout !== updatedPublicData.calendarLayout;
        const isLocationsSelectionChanged =
          publicData.selectedLocations !== updatedPublicData.selectedLocations;
        const isCategoriesSelectionChanged =
          publicData.selectedCategories !==
          updatedPublicData.selectedCategories;
        const isServiceSelectedChanged =
          publicData.selectedService !== updatedPublicData.selectedService;
        const isSlotsAvailabilityChanged =
          publicData.slotsAvailability !== updatedPublicData.slotsAvailability;
        publicData = updatedPublicData;

        if (
          !isCalendarPagePreset &&
          (isLocationsSelectionChanged ||
            isCategoriesSelectionChanged ||
            isServiceSelectedChanged ||
            isSlotsAvailabilityChanged)
        ) {
          pageReady();
        } else {
          await rerender({
            resetState: isCalendarLayoutChanged,
          });
        }
      },
    };
  };

  return createController;
};

const isDateAndTimeMode = async ({
  flowAPI,
  preset,
}: {
  flowAPI: ControllerFlowAPI;
  preset: Preset;
}) => {
  if (!isCalendarPage(preset)) {
    return false;
  }

  const serviceSlug = await getServiceSlug({
    wixCodeApi: flowAPI.controllerConfig.wixCodeApi,
    pageName: CALENDAR_PAGE_URL_PATH_PARAM,
  });

  return serviceSlug === ReservedSlug.DATE_AND_TIME;
};